import styled from 'styled-components';

export const Container = styled.div``;

/* ── the employee cell in the detail grid — the same shape ticket detail uses ── */

export const Person = styled.div`
  display: flex;
  align-items: center;
  gap: var(--s2);
  min-width: 0;
`;

export const PersonName = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

/* ══════════════════════════ the cart ══════════════════════════
 *
 * The richest data in the product, and the only place it is ever shown. An
 * order is a list of foods; each food carries a quantity; each food may carry
 * option groups, and each group is one configuration — a set of chosen
 * options plus how many were asked for that way.
 *
 * It is drawn as a list rather than as a stack of cards. The card is the
 * container, and its `flush` body lets each line meet the card's edge and be
 * separated by one hairline, which is the design's own list treatment (the
 * detail grid, the table, the dropdown panel). A card per line would have put
 * a border, a radius and a shadow around every coffee.
 * ═══════════════════════════════════════════════════════════════ */

export const Lines = styled.div`
  display: flex;
  flex-direction: column;
`;

export const Line = styled.div`
  padding: var(--s3) var(--s5);

  & + & {
    border-top: 1px solid var(--line);
  }
`;

export const LineHead = styled.div`
  display: flex;
  align-items: center;
  gap: var(--s3);
  min-width: 0;
`;

export const LineTitle = styled.div`
  flex: 1;
  min-width: 0;
  font-family: var(--font-ui);
  font-size: var(--fs-body);
  font-weight: var(--fw-medium);
  line-height: var(--lh-body);
  color: var(--text);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

/** How many. Mono with tabular numerals, like every other count in the panel. */
export const Qty = styled.span`
  flex: 0 0 auto;
  font-family: var(--font-mono);
  font-size: var(--fs-mono);
  font-variant-numeric: tabular-nums;
  color: var(--text);
`;

/**
 * The configurations under a line.
 *
 * Indented past the thumbnail so they read as belonging to the item above
 * them rather than as siblings of it: --h-thumb plus the head's gap.
 */
export const Groups = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--s2);
  margin-top: var(--s2);
  padding-left: calc(var(--h-thumb) + var(--s3));
`;

export const Group = styled.div`
  display: flex;
  align-items: center;
  gap: var(--s2);
`;

export const GroupQty = styled.span`
  flex: 0 0 auto;
  font-family: var(--font-mono);
  font-size: var(--fs-mono);
  font-variant-numeric: tabular-nums;
  color: var(--text-muted);
`;

export const Options = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: var(--s1);
  min-width: 0;
`;

/**
 * One option and what it was set to.
 *
 * Name and value are one chip, split by a hairline, so the pair cannot be
 * misread across a wrap the way `Sugar: Regular, Milk: No Milk` can. The
 * value carries the weight; the name is the quieter half.
 */
export const Option = styled.span`
  display: inline-flex;
  align-items: center;
  border: 1px solid var(--line);
  border-radius: var(--r-sm);
  background: var(--surface-alt);
  font-family: var(--font-ui);
  font-size: var(--fs-micro);
  line-height: var(--lh-body);
  white-space: nowrap;
`;

export const OptionName = styled.span`
  padding: var(--s1) var(--s2);
  color: var(--text-faint);
`;

export const OptionValue = styled.span`
  padding: var(--s1) var(--s2);
  border-left: 1px solid var(--line);
  font-weight: var(--fw-medium);
  color: var(--text);
`;

/* ══════════════════════ Call the Waiter ══════════════════════
 *
 * Deliberately not an empty state. Nothing is missing here — the employee
 * asked for a person rather than for a drink, and the screen should say so
 * in the shape it says other true things: the design's `.person` row, an
 * icon and two lines, on the card's own surface.
 * ═══════════════════════════════════════════════════════════════ */

export const Waiter = styled.div`
  display: flex;
  align-items: center;
  gap: var(--s3);
`;

export const WaiterIcon = styled.div`
  display: grid;
  place-items: center;
  width: 44px;
  height: 44px;
  flex: 0 0 44px;
  border-radius: var(--r-md);
  border: 1px solid var(--line);
  background: var(--surface-alt);
  color: var(--text-faint);

  svg {
    width: 20px;
    height: 20px;
    stroke-width: 1.6;
  }
`;

export const WaiterTitle = styled.div`
  font-family: var(--font-ui);
  font-size: var(--fs-body);
  font-weight: var(--fw-semibold);
  line-height: var(--lh-body);
  color: var(--text);
`;

export const WaiterText = styled.div`
  font-family: var(--font-ui);
  font-size: var(--fs-body);
  font-weight: var(--fw-regular);
  line-height: var(--lh-body);
  color: var(--text-muted);
`;
